import type { Language, Locale } from '@/config/locales';

export interface ProfileTranslations {
  title: string;
  fullName: string;
  phone: string;
  email: string;
  birthday: string;
  gender: string;
  selectGender: string;
  genderMale: string;
  genderFemale: string;
  genderOther: string;
  genderPreferNotToSay: string;
  edit: string;
  save: string;
  cancel: string;
  verifiedCompany: string;
  companyVerified: string;
  verifyEmail: string;
  verifyEmailDesc: string;
  changePhoto: string;
  uploadPhoto: string;
  chooseAvatar: string;
  selectAvatar: string;
  selectDate: string;
  birthdayConfirmTitle: string;
  birthdayConfirmMessage: string;
  birthdayWarning: string;
  birthdayLocked: string;
  confirm: string;
  selectedDate: string;
  unsavedChangesTitle: string;
  unsavedChangesMessage: string;
  saveAndExit: string;
  discardChanges: string;
  profileSaved: string;
  savingProfile: string;
  or: string;
  selectFromGallery: string;
}

// #522 — Locale 단일 출처 정합 (15언어 전수).
export const profileTranslations:
  & Record<Language, ProfileTranslations>
  & Record<Exclude<Locale, Language>, ProfileTranslations> = {
  ko: {
    title: '내 프로필',
    fullName: '이름',
    phone: '전화번호',
    email: '이메일',
    birthday: '생년월일',
    gender: '성별',
    selectGender: '성별 선택',
    genderMale: '남성',
    genderFemale: '여성',
    genderOther: '기타',
    genderPreferNotToSay: '응답하지 않음',
    edit: '편집',
    save: '저장',
    cancel: '취소',
    verifiedCompany: '인증된 기업',
    companyVerified: '기업 인증 완료',
    verifyEmail: '기업 이메일 인증',
    verifyEmailDesc: '기업 이메일을 인증하고 특별 혜택을 받으세요',
    changePhoto: '사진 변경',
    uploadPhoto: '사진 업로드',
    chooseAvatar: '캐릭터 선택',
    selectAvatar: '아바타 선택',
    selectDate: '날짜 선택',
    birthdayConfirmTitle: '생년월일 등록 확인',
    birthdayConfirmMessage: '생년월일은 한 번 등록하면 변경할 수 없습니다.\n정말로 이 날짜로 등록하시겠습니까?',
    birthdayWarning: '생년월일은 등록 후 수정할 수 없습니다',
    birthdayLocked: '생년월일이 등록되어 있습니다',
    confirm: '확인',
    selectedDate: '선택한 날짜',
    unsavedChangesTitle: '저장하지 않은 변경사항',
    unsavedChangesMessage: '저장하지 않은 변경사항이 있습니다.\n저장하시겠습니까?',
    saveAndExit: '저장하고 나가기',
    discardChanges: '변경사항 취소',
    profileSaved: '프로필이 저장되었습니다',
    savingProfile: '저장 중...',
    or: '또는',
    selectFromGallery: '갤러리에서 사진 선택'
  },
  es: {
    title: 'Mi perfil',
    fullName: 'Nombre completo',
    phone: 'Teléfono',
    email: 'Correo electrónico',
    birthday: 'Fecha de nacimiento',
    gender: 'Género',
    selectGender: 'Seleccionar género',
    genderMale: 'Masculino',
    genderFemale: 'Femenino',
    genderOther: 'Otro',
    genderPreferNotToSay: 'Prefiero no decirlo',
    edit: 'Editar',
    save: 'Guardar',
    cancel: 'Cancelar',
    verifiedCompany: 'Empresa verificada',
    companyVerified: 'Empresa verificada',
    verifyEmail: 'Verificar correo corporativo',
    verifyEmailDesc: 'Verifica tu correo corporativo y obtén beneficios especiales',
    changePhoto: 'Cambiar foto',
    uploadPhoto: 'Subir foto',
    chooseAvatar: 'Elegir personaje',
    selectAvatar: 'Seleccionar avatar',
    selectDate: 'Seleccionar fecha',
    birthdayConfirmTitle: 'Confirmar fecha de nacimiento',
    birthdayConfirmMessage: 'La fecha de nacimiento no se puede modificar después del registro.\n¿Realmente desea registrar esta fecha?',
    birthdayWarning: 'La fecha de nacimiento no se puede modificar después del registro',
    birthdayLocked: 'Fecha de nacimiento registrada',
    confirm: 'Confirmar',
    selectedDate: 'Fecha seleccionada',
    unsavedChangesTitle: 'Cambios sin guardar',
    unsavedChangesMessage: 'Hay cambios sin guardar.\n¿Desea guardarlos?',
    saveAndExit: 'Guardar y salir',
    discardChanges: 'Descartar cambios',
    profileSaved: 'Perfil guardado',
    savingProfile: 'Guardando...',
    or: 'o',
    selectFromGallery: 'Seleccionar de galería'
  },
  en: {
    title: 'My Profile',
    fullName: 'Full Name',
    phone: 'Phone',
    email: 'Email',
    birthday: 'Birthday',
    gender: 'Gender',
    selectGender: 'Select gender',
    genderMale: 'Male',
    genderFemale: 'Female',
    genderOther: 'Other',
    genderPreferNotToSay: 'Prefer not to say',
    edit: 'Edit',
    save: 'Save',
    cancel: 'Cancel',
    verifiedCompany: 'Verified Company',
    companyVerified: 'Company Verified',
    verifyEmail: 'Verify Corporate Email',
    verifyEmailDesc: 'Verify your corporate email and get special benefits',
    changePhoto: 'Change Photo',
    uploadPhoto: 'Upload Photo',
    chooseAvatar: 'Choose Character',
    selectAvatar: 'Select Avatar',
    selectDate: 'Select date',
    birthdayConfirmTitle: 'Confirm Birthday',
    birthdayConfirmMessage: 'Birthday cannot be modified after registration.\nDo you really want to register this date?',
    birthdayWarning: 'Birthday cannot be modified after registration',
    birthdayLocked: 'Birthday is registered',
    confirm: 'Confirm',
    selectedDate: 'Selected date',
    unsavedChangesTitle: 'Unsaved Changes',
    unsavedChangesMessage: 'You have unsaved changes.\nDo you want to save them?',
    saveAndExit: 'Save and Exit',
    discardChanges: 'Discard Changes',
    profileSaved: 'Profile saved',
    savingProfile: 'Saving...',
    or: 'or',
    selectFromGallery: 'Select from gallery'
  },
  pt: {
    title: 'Meu perfil',
    fullName: 'Nome completo',
    phone: 'Telefone',
    email: 'E-mail',
    birthday: 'Data de nascimento',
    gender: 'Gênero',
    selectGender: 'Selecionar gênero',
    genderMale: 'Masculino',
    genderFemale: 'Feminino',
    genderOther: 'Outro',
    genderPreferNotToSay: 'Prefiro não dizer',
    edit: 'Editar',
    save: 'Salvar',
    cancel: 'Cancelar',
    verifiedCompany: 'Empresa verificada',
    companyVerified: 'Empresa verificada',
    verifyEmail: 'Verificar e-mail corporativo',
    verifyEmailDesc: 'Verifique seu e-mail corporativo e receba benefícios especiais',
    changePhoto: 'Alterar foto',
    uploadPhoto: 'Enviar foto',
    chooseAvatar: 'Escolher personagem',
    selectAvatar: 'Selecionar avatar',
    selectDate: 'Selecionar data',
    birthdayConfirmTitle: 'Confirmar data de nascimento',
    birthdayConfirmMessage: 'A data de nascimento não pode ser alterada após o registro.\nDeseja realmente registrar esta data?',
    birthdayWarning: 'A data de nascimento não pode ser alterada após o registro',
    birthdayLocked: 'Data de nascimento registrada',
    confirm: 'Confirmar',
    selectedDate: 'Data selecionada',
    unsavedChangesTitle: 'Alterações não salvas',
    unsavedChangesMessage: 'Há alterações não salvas.\nDeseja salvá-las?',
    saveAndExit: 'Salvar e sair',
    discardChanges: 'Descartar alterações',
    profileSaved: 'Perfil salvo',
    savingProfile: 'Salvando...',
    or: 'ou',
    selectFromGallery: 'Selecionar da galeria'
  },
  fr: {
    title: 'Mon profil',
    fullName: 'Nom complet',
    phone: 'Téléphone',
    email: 'E-mail',
    birthday: 'Date de naissance',
    gender: 'Genre',
    selectGender: 'Sélectionner le genre',
    genderMale: 'Homme',
    genderFemale: 'Femme',
    genderOther: 'Autre',
    genderPreferNotToSay: 'Préfère ne pas répondre',
    edit: 'Modifier',
    save: 'Enregistrer',
    cancel: 'Annuler',
    verifiedCompany: 'Entreprise vérifiée',
    companyVerified: 'Entreprise vérifiée',
    verifyEmail: 'Vérifier l’e-mail professionnel',
    verifyEmailDesc: 'Vérifiez votre e-mail professionnel et bénéficiez d’avantages spéciaux',
    changePhoto: 'Changer la photo',
    uploadPhoto: 'Télécharger une photo',
    chooseAvatar: 'Choisir un personnage',
    selectAvatar: 'Sélectionner un avatar',
    selectDate: 'Sélectionner une date',
    birthdayConfirmTitle: 'Confirmer la date de naissance',
    birthdayConfirmMessage: 'La date de naissance ne peut pas être modifiée après l’enregistrement.\nVoulez-vous vraiment enregistrer cette date ?',
    birthdayWarning: 'La date de naissance ne peut pas être modifiée après l’enregistrement',
    birthdayLocked: 'Date de naissance enregistrée',
    confirm: 'Confirmer',
    selectedDate: 'Date sélectionnée',
    unsavedChangesTitle: 'Modifications non enregistrées',
    unsavedChangesMessage: 'Vous avez des modifications non enregistrées.\nVoulez-vous les enregistrer ?',
    saveAndExit: 'Enregistrer et quitter',
    discardChanges: 'Annuler les modifications',
    profileSaved: 'Profil enregistré',
    savingProfile: 'Enregistrement...',
    or: 'ou',
    selectFromGallery: 'Sélectionner depuis la galerie'
  },
  zh: {
    title: '我的资料',
    fullName: '姓名',
    phone: '电话号码',
    email: '电子邮箱',
    birthday: '出生日期',
    gender: '性别',
    selectGender: '选择性别',
    genderMale: '男',
    genderFemale: '女',
    genderOther: '其他',
    genderPreferNotToSay: '不愿透露',
    edit: '编辑',
    save: '保存',
    cancel: '取消',
    verifiedCompany: '认证企业',
    companyVerified: '企业认证完成',
    verifyEmail: '验证企业邮箱',
    verifyEmailDesc: '验证企业邮箱即可获得特别优惠',
    changePhoto: '更换照片',
    uploadPhoto: '上传照片',
    chooseAvatar: '选择角色',
    selectAvatar: '选择头像',
    selectDate: '选择日期',
    birthdayConfirmTitle: '确认出生日期',
    birthdayConfirmMessage: '出生日期注册后无法修改。\n确定要注册此日期吗？',
    birthdayWarning: '出生日期注册后无法修改',
    birthdayLocked: '出生日期已注册',
    confirm: '确认',
    selectedDate: '已选日期',
    unsavedChangesTitle: '未保存的更改',
    unsavedChangesMessage: '有未保存的更改。\n是否保存？',
    saveAndExit: '保存并退出',
    discardChanges: '放弃更改',
    profileSaved: '资料已保存',
    savingProfile: '保存中...',
    or: '或',
    selectFromGallery: '从相册选择照片'
  },
  ja: {
    title: 'マイプロフィール',
    fullName: '氏名',
    phone: '電話番号',
    email: 'メールアドレス',
    birthday: '生年月日',
    gender: '性別',
    selectGender: '性別を選択',
    genderMale: '男性',
    genderFemale: '女性',
    genderOther: 'その他',
    genderPreferNotToSay: '回答しない',
    edit: '編集',
    save: '保存',
    cancel: 'キャンセル',
    verifiedCompany: '認証済み企業',
    companyVerified: '企業認証済み',
    verifyEmail: '企業メール認証',
    verifyEmailDesc: '企業メールを認証して特別特典を受け取りましょう',
    changePhoto: '写真を変更',
    uploadPhoto: '写真をアップロード',
    chooseAvatar: 'キャラクターを選択',
    selectAvatar: 'アバターを選択',
    selectDate: '日付を選択',
    birthdayConfirmTitle: '生年月日の登録確認',
    birthdayConfirmMessage: '生年月日は一度登録すると変更できません。\n本当にこの日付で登録しますか？',
    birthdayWarning: '生年月日は登録後に変更できません',
    birthdayLocked: '生年月日は登録済みです',
    confirm: '確認',
    selectedDate: '選択した日付',
    unsavedChangesTitle: '未保存の変更',
    unsavedChangesMessage: '未保存の変更があります。\n保存しますか？',
    saveAndExit: '保存して終了',
    discardChanges: '変更を破棄',
    profileSaved: 'プロフィールを保存しました',
    savingProfile: '保存中...',
    or: 'または',
    selectFromGallery: 'ギャラリーから写真を選択'
  },
  hi: {
    title: 'मेरी प्रोफ़ाइल',
    fullName: 'पूरा नाम',
    phone: 'फ़ोन',
    email: 'ईमेल',
    birthday: 'जन्म तिथि',
    gender: 'लिंग',
    selectGender: 'लिंग चुनें',
    genderMale: 'पुरुष',
    genderFemale: 'महिला',
    genderOther: 'अन्य',
    genderPreferNotToSay: 'बताना नहीं चाहते',
    edit: 'संपादित करें',
    save: 'सहेजें',
    cancel: 'रद्द करें',
    verifiedCompany: 'सत्यापित कंपनी',
    companyVerified: 'कंपनी सत्यापित',
    verifyEmail: 'कॉर्पोरेट ईमेल सत्यापित करें',
    verifyEmailDesc: 'अपना कॉर्पोरेट ईमेल सत्यापित करें और विशेष लाभ पाएं',
    changePhoto: 'फ़ोटो बदलें',
    uploadPhoto: 'फ़ोटो अपलोड करें',
    chooseAvatar: 'किरदार चुनें',
    selectAvatar: 'अवतार चुनें',
    selectDate: 'तिथि चुनें',
    birthdayConfirmTitle: 'जन्म तिथि की पुष्टि करें',
    birthdayConfirmMessage: 'पंजीकरण के बाद जन्म तिथि बदली नहीं जा सकती।\nक्या आप वाकई यह तिथि पंजीकृत करना चाहते हैं?',
    birthdayWarning: 'पंजीकरण के बाद जन्म तिथि बदली नहीं जा सकती',
    birthdayLocked: 'जन्म तिथि पंजीकृत है',
    confirm: 'पुष्टि करें',
    selectedDate: 'चुनी गई तिथि',
    unsavedChangesTitle: 'सहेजे नहीं गए परिवर्तन',
    unsavedChangesMessage: 'आपके पास सहेजे नहीं गए परिवर्तन हैं।\nक्या आप उन्हें सहेजना चाहते हैं?',
    saveAndExit: 'सहेजें और बाहर निकलें',
    discardChanges: 'परिवर्तन त्यागें',
    profileSaved: 'प्रोफ़ाइल सहेजी गई',
    savingProfile: 'सहेजा जा रहा है...',
    or: 'या',
    selectFromGallery: 'गैलरी से चुनें'
  },
  de: {
    title: 'Mein Profil',
    fullName: 'Vollständiger Name',
    phone: 'Telefon',
    email: 'E-Mail',
    birthday: 'Geburtsdatum',
    gender: 'Geschlecht',
    selectGender: 'Geschlecht auswählen',
    genderMale: 'Männlich',
    genderFemale: 'Weiblich',
    genderOther: 'Divers',
    genderPreferNotToSay: 'Keine Angabe',
    edit: 'Bearbeiten',
    save: 'Speichern',
    cancel: 'Abbrechen',
    verifiedCompany: 'Verifiziertes Unternehmen',
    companyVerified: 'Unternehmen verifiziert',
    verifyEmail: 'Firmen-E-Mail verifizieren',
    verifyEmailDesc: 'Verifizieren Sie Ihre Firmen-E-Mail und erhalten Sie besondere Vorteile',
    changePhoto: 'Foto ändern',
    uploadPhoto: 'Foto hochladen',
    chooseAvatar: 'Charakter wählen',
    selectAvatar: 'Avatar auswählen',
    selectDate: 'Datum auswählen',
    birthdayConfirmTitle: 'Geburtsdatum bestätigen',
    birthdayConfirmMessage: 'Das Geburtsdatum kann nach der Registrierung nicht geändert werden.\nMöchten Sie dieses Datum wirklich registrieren?',
    birthdayWarning: 'Das Geburtsdatum kann nach der Registrierung nicht geändert werden',
    birthdayLocked: 'Geburtsdatum ist registriert',
    confirm: 'Bestätigen',
    selectedDate: 'Ausgewähltes Datum',
    unsavedChangesTitle: 'Nicht gespeicherte Änderungen',
    unsavedChangesMessage: 'Es gibt nicht gespeicherte Änderungen.\nMöchten Sie sie speichern?',
    saveAndExit: 'Speichern und verlassen',
    discardChanges: 'Änderungen verwerfen',
    profileSaved: 'Profil gespeichert',
    savingProfile: 'Wird gespeichert...',
    or: 'oder',
    selectFromGallery: 'Aus der Galerie auswählen'
  },
  it: {
    title: 'Il mio profilo',
    fullName: 'Nome completo',
    phone: 'Telefono',
    email: 'Email',
    birthday: 'Data di nascita',
    gender: 'Genere',
    selectGender: 'Seleziona genere',
    genderMale: 'Maschile',
    genderFemale: 'Femminile',
    genderOther: 'Altro',
    genderPreferNotToSay: 'Preferisco non rispondere',
    edit: 'Modifica',
    save: 'Salva',
    cancel: 'Annulla',
    verifiedCompany: 'Azienda verificata',
    companyVerified: 'Azienda verificata',
    verifyEmail: 'Verifica email aziendale',
    verifyEmailDesc: 'Verifica la tua email aziendale e ottieni vantaggi speciali',
    changePhoto: 'Cambia foto',
    uploadPhoto: 'Carica foto',
    chooseAvatar: 'Scegli personaggio',
    selectAvatar: 'Seleziona avatar',
    selectDate: 'Seleziona data',
    birthdayConfirmTitle: 'Conferma data di nascita',
    birthdayConfirmMessage: 'La data di nascita non può essere modificata dopo la registrazione.\nVuoi davvero registrare questa data?',
    birthdayWarning: 'La data di nascita non può essere modificata dopo la registrazione',
    birthdayLocked: 'Data di nascita registrata',
    confirm: 'Conferma',
    selectedDate: 'Data selezionata',
    unsavedChangesTitle: 'Modifiche non salvate',
    unsavedChangesMessage: 'Ci sono modifiche non salvate.\nVuoi salvarle?',
    saveAndExit: 'Salva ed esci',
    discardChanges: 'Annulla le modifiche',
    profileSaved: 'Profilo salvato',
    savingProfile: 'Salvataggio...',
    or: 'o',
    selectFromGallery: 'Seleziona dalla galleria'
  },
  ru: {
    title: 'Мой профиль',
    fullName: 'Полное имя',
    phone: 'Телефон',
    email: 'Эл. почта',
    birthday: 'Дата рождения',
    gender: 'Пол',
    selectGender: 'Выберите пол',
    genderMale: 'Мужской',
    genderFemale: 'Женский',
    genderOther: 'Другое',
    genderPreferNotToSay: 'Предпочитаю не указывать',
    edit: 'Редактировать',
    save: 'Сохранить',
    cancel: 'Отмена',
    verifiedCompany: 'Проверенная компания',
    companyVerified: 'Компания подтверждена',
    verifyEmail: 'Подтвердить корпоративную почту',
    verifyEmailDesc: 'Подтвердите корпоративную почту и получите особые преимущества',
    changePhoto: 'Изменить фото',
    uploadPhoto: 'Загрузить фото',
    chooseAvatar: 'Выбрать персонажа',
    selectAvatar: 'Выбрать аватар',
    selectDate: 'Выбрать дату',
    birthdayConfirmTitle: 'Подтверждение даты рождения',
    birthdayConfirmMessage: 'Дату рождения нельзя изменить после регистрации.\nВы действительно хотите зарегистрировать эту дату?',
    birthdayWarning: 'Дату рождения нельзя изменить после регистрации',
    birthdayLocked: 'Дата рождения зарегистрирована',
    confirm: 'Подтвердить',
    selectedDate: 'Выбранная дата',
    unsavedChangesTitle: 'Несохранённые изменения',
    unsavedChangesMessage: 'Есть несохранённые изменения.\nСохранить их?',
    saveAndExit: 'Сохранить и выйти',
    discardChanges: 'Отменить изменения',
    profileSaved: 'Профиль сохранён',
    savingProfile: 'Сохранение...',
    or: 'или',
    selectFromGallery: 'Выбрать из галереи'
  },
  ar: {
    title: 'ملفي الشخصي',
    fullName: 'الاسم الكامل',
    phone: 'الهاتف',
    email: 'البريد الإلكتروني',
    birthday: 'تاريخ الميلاد',
    gender: 'الجنس',
    selectGender: 'اختر الجنس',
    genderMale: 'ذكر',
    genderFemale: 'أنثى',
    genderOther: 'آخر',
    genderPreferNotToSay: 'أفضل عدم الإجابة',
    edit: 'تعديل',
    save: 'حفظ',
    cancel: 'إلغاء',
    verifiedCompany: 'شركة موثقة',
    companyVerified: 'تم توثيق الشركة',
    verifyEmail: 'توثيق البريد الإلكتروني للشركة',
    verifyEmailDesc: 'وثّق بريدك الإلكتروني للشركة واحصل على مزايا خاصة',
    changePhoto: 'تغيير الصورة',
    uploadPhoto: 'رفع صورة',
    chooseAvatar: 'اختيار شخصية',
    selectAvatar: 'اختيار الصورة الرمزية',
    selectDate: 'اختر التاريخ',
    birthdayConfirmTitle: 'تأكيد تاريخ الميلاد',
    birthdayConfirmMessage: 'لا يمكن تغيير تاريخ الميلاد بعد التسجيل.\nهل تريد حقًا تسجيل هذا التاريخ؟',
    birthdayWarning: 'لا يمكن تغيير تاريخ الميلاد بعد التسجيل',
    birthdayLocked: 'تاريخ الميلاد مسجل',
    confirm: 'تأكيد',
    selectedDate: 'التاريخ المحدد',
    unsavedChangesTitle: 'تغييرات غير محفوظة',
    unsavedChangesMessage: 'لديك تغييرات غير محفوظة.\nهل تريد حفظها؟',
    saveAndExit: 'حفظ وخروج',
    discardChanges: 'تجاهل التغييرات',
    profileSaved: 'تم حفظ الملف الشخصي',
    savingProfile: 'جارٍ الحفظ...',
    or: 'أو',
    selectFromGallery: 'اختيار من المعرض'
  },
  vi: {
    title: 'Hồ sơ của tôi',
    fullName: 'Họ và tên',
    phone: 'Số điện thoại',
    email: 'Email',
    birthday: 'Ngày sinh',
    gender: 'Giới tính',
    selectGender: 'Chọn giới tính',
    genderMale: 'Nam',
    genderFemale: 'Nữ',
    genderOther: 'Khác',
    genderPreferNotToSay: 'Không muốn trả lời',
    edit: 'Chỉnh sửa',
    save: 'Lưu',
    cancel: 'Hủy',
    verifiedCompany: 'Doanh nghiệp đã xác minh',
    companyVerified: 'Đã xác minh doanh nghiệp',
    verifyEmail: 'Xác minh email công ty',
    verifyEmailDesc: 'Xác minh email công ty của bạn để nhận ưu đãi đặc biệt',
    changePhoto: 'Đổi ảnh',
    uploadPhoto: 'Tải ảnh lên',
    chooseAvatar: 'Chọn nhân vật',
    selectAvatar: 'Chọn ảnh đại diện',
    selectDate: 'Chọn ngày',
    birthdayConfirmTitle: 'Xác nhận ngày sinh',
    birthdayConfirmMessage: 'Ngày sinh không thể thay đổi sau khi đăng ký.\nBạn có chắc muốn đăng ký ngày này không?',
    birthdayWarning: 'Ngày sinh không thể thay đổi sau khi đăng ký',
    birthdayLocked: 'Ngày sinh đã được đăng ký',
    confirm: 'Xác nhận',
    selectedDate: 'Ngày đã chọn',
    unsavedChangesTitle: 'Thay đổi chưa lưu',
    unsavedChangesMessage: 'Có thay đổi chưa được lưu.\nBạn có muốn lưu không?',
    saveAndExit: 'Lưu và thoát',
    discardChanges: 'Hủy thay đổi',
    profileSaved: 'Đã lưu hồ sơ',
    savingProfile: 'Đang lưu...',
    or: 'hoặc',
    selectFromGallery: 'Chọn từ thư viện ảnh'
  },
  th: {
    title: 'โปรไฟล์ของฉัน',
    fullName: 'ชื่อ-นามสกุล',
    phone: 'โทรศัพท์',
    email: 'อีเมล',
    birthday: 'วันเกิด',
    gender: 'เพศ',
    selectGender: 'เลือกเพศ',
    genderMale: 'ชาย',
    genderFemale: 'หญิง',
    genderOther: 'อื่น ๆ',
    genderPreferNotToSay: 'ไม่ต้องการระบุ',
    edit: 'แก้ไข',
    save: 'บันทึก',
    cancel: 'ยกเลิก',
    verifiedCompany: 'บริษัทที่ได้รับการยืนยัน',
    companyVerified: 'ยืนยันบริษัทแล้ว',
    verifyEmail: 'ยืนยันอีเมลบริษัท',
    verifyEmailDesc: 'ยืนยันอีเมลบริษัทของคุณเพื่อรับสิทธิประโยชน์พิเศษ',
    changePhoto: 'เปลี่ยนรูปภาพ',
    uploadPhoto: 'อัปโหลดรูปภาพ',
    chooseAvatar: 'เลือกตัวละคร',
    selectAvatar: 'เลือกอวาตาร์',
    selectDate: 'เลือกวันที่',
    birthdayConfirmTitle: 'ยืนยันวันเกิด',
    birthdayConfirmMessage: 'วันเกิดไม่สามารถแก้ไขได้หลังจากลงทะเบียน\nคุณต้องการลงทะเบียนวันที่นี้จริงหรือไม่?',
    birthdayWarning: 'วันเกิดไม่สามารถแก้ไขได้หลังจากลงทะเบียน',
    birthdayLocked: 'ลงทะเบียนวันเกิดแล้ว',
    confirm: 'ยืนยัน',
    selectedDate: 'วันที่ที่เลือก',
    unsavedChangesTitle: 'การเปลี่ยนแปลงที่ยังไม่ได้บันทึก',
    unsavedChangesMessage: 'มีการเปลี่ยนแปลงที่ยังไม่ได้บันทึก\nต้องการบันทึกหรือไม่?',
    saveAndExit: 'บันทึกแล้วออก',
    discardChanges: 'ยกเลิกการเปลี่ยนแปลง',
    profileSaved: 'บันทึกโปรไฟล์แล้ว',
    savingProfile: 'กำลังบันทึก...',
    or: 'หรือ',
    selectFromGallery: 'เลือกจากแกลเลอรี'
  },
  nl: {
    title: 'Mijn profiel',
    fullName: 'Volledige naam',
    phone: 'Telefoon',
    email: 'E-mail',
    birthday: 'Geboortedatum',
    gender: 'Geslacht',
    selectGender: 'Geslacht selecteren',
    genderMale: 'Man',
    genderFemale: 'Vrouw',
    genderOther: 'Anders',
    genderPreferNotToSay: 'Zeg ik liever niet',
    edit: 'Bewerken',
    save: 'Opslaan',
    cancel: 'Annuleren',
    verifiedCompany: 'Geverifieerd bedrijf',
    companyVerified: 'Bedrijf geverifieerd',
    verifyEmail: 'Zakelijke e-mail verifiëren',
    verifyEmailDesc: 'Verifieer uw zakelijke e-mail en ontvang speciale voordelen',
    changePhoto: 'Foto wijzigen',
    uploadPhoto: 'Foto uploaden',
    chooseAvatar: 'Personage kiezen',
    selectAvatar: 'Avatar selecteren',
    selectDate: 'Datum selecteren',
    birthdayConfirmTitle: 'Geboortedatum bevestigen',
    birthdayConfirmMessage: 'De geboortedatum kan na registratie niet meer worden gewijzigd.\nWilt u deze datum echt registreren?',
    birthdayWarning: 'De geboortedatum kan na registratie niet meer worden gewijzigd',
    birthdayLocked: 'Geboortedatum is geregistreerd',
    confirm: 'Bevestigen',
    selectedDate: 'Geselecteerde datum',
    unsavedChangesTitle: 'Niet-opgeslagen wijzigingen',
    unsavedChangesMessage: 'Er zijn niet-opgeslagen wijzigingen.\nWilt u ze opslaan?',
    saveAndExit: 'Opslaan en afsluiten',
    discardChanges: 'Wijzigingen verwerpen',
    profileSaved: 'Profiel opgeslagen',
    savingProfile: 'Opslaan...',
    or: 'of',
    selectFromGallery: 'Selecteren uit galerij'
  }
};

// 미리 정의된 캐릭터 아바타들
export const avatarCharacters = [
  { id: 'avatar-1', url: 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=200&h=200&fit=crop', name: 'Character 1' },
  { id: 'avatar-2', url: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=200&h=200&fit=crop', name: 'Character 2' },
  { id: 'avatar-3', url: 'https://images.unsplash.com/photo-1599566150163-29194dcaad36?w=200&h=200&fit=crop', name: 'Character 3' },
  { id: 'avatar-4', url: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&h=200&fit=crop', name: 'Character 4' },
  { id: 'avatar-5', url: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=200&h=200&fit=crop', name: 'Character 5' },
  { id: 'avatar-6', url: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200&h=200&fit=crop', name: 'Character 6' },
  { id: 'avatar-7', url: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=200&h=200&fit=crop', name: 'Character 7' },
  { id: 'avatar-8', url: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=200&h=200&fit=crop', name: 'Character 8' },
  { id: 'avatar-9', url: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=200&h=200&fit=crop', name: 'Character 9' }
];

// 기업 이름 매핑
export const companyNames: Record<string, string> = {
  samsung: 'Samsung',
  kakao: 'Kakao',
  naver: 'Naver'
};
